import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, Package, PlusCircle, UserCog } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { OrderCard } from "@/components/account/OrderCard";
import { ProfileForm } from "@/components/account/ProfileForm";
import { initialsOf } from "@/components/nav/AccountMenu";

export default function CustomerPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/cuenta", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/customers/me/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, [user]);

  const doLogout = async () => {
    navigate("/");
    await logout();
  };

  if (!user) {
    return (
      <div className="bg-slate-50 min-h-screen">
        <Navbar />
        <div className="pt-48 flex justify-center" data-testid="customer-panel-loading">
          <Loader2 className="h-8 w-8 text-cyan-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="customer-panel">
      <Navbar />
      <main className="pt-24 sm:pt-28 pb-12 sm:pb-20 max-w-6xl mx-auto px-4 sm:px-6">
        <header className="flex flex-wrap items-center justify-between gap-4 sm:gap-5 mb-8 sm:mb-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <Avatar className="h-12 w-12 sm:h-14 sm:w-14 ring-4 ring-white shadow-md">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-cyan-100 text-cyan-700 font-semibold">{initialsOf(user.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 notranslate" data-testid="customer-panel-greeting">
                Hola, {user.name?.split(" ")[0]}
              </h1>
              <p className="text-sm text-slate-500 notranslate truncate">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button asChild data-testid="customer-new-order-btn" className="rounded-full bg-cyan-500 hover:bg-cyan-600 px-5 h-10 flex-1 sm:flex-none">
              <Link to="/reservar"><PlusCircle className="h-4 w-4 mr-2" /> Nuevo pedido</Link>
            </Button>
            <Button variant="outline" onClick={doLogout} data-testid="customer-logout-btn" className="rounded-full h-10">
              <LogOut className="h-4 w-4 mr-2" /> Salir
            </Button>
          </div>
        </header>

        <Tabs defaultValue={params.get("tab") === "datos" ? "datos" : "pedidos"}>
          <TabsList className="mb-6 bg-white border border-slate-200 rounded-full p-1 h-auto grid grid-cols-2 w-full sm:inline-flex sm:w-auto">
            <TabsTrigger value="pedidos" data-testid="customer-tab-pedidos" className="rounded-full px-3 sm:px-5 py-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
              <Package className="h-4 w-4 mr-2" /> Mis pedidos {orders && `(${orders.length})`}
            </TabsTrigger>
            <TabsTrigger value="datos" data-testid="customer-tab-datos" className="rounded-full px-3 sm:px-5 py-2 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
              <UserCog className="h-4 w-4 mr-2" /> Mis datos
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pedidos">
            {orders === null ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-cyan-500 animate-spin" /></div>
            ) : orders.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="customer-orders-empty">
                <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="font-heading font-semibold text-slate-900">Todavía no tienes pedidos</p>
                <p className="text-sm text-slate-500 mt-1">Reserva tu primera recogida y la verás aquí con su estado en tiempo real.</p>
                <Button asChild className="mt-5 rounded-full bg-cyan-500 hover:bg-cyan-600 px-6">
                  <Link to="/reservar">Reservar recogida</Link>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="customer-orders-list">
                {orders.map((o) => <OrderCard key={o.id} order={o} />)}
              </div>
            )}
          </TabsContent>
          <TabsContent value="datos">
            <ProfileForm />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
